import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach token + active business unit
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Inject active business unit so backend filters data correctly
    const activeUnit = localStorage.getItem('activeUnit') || 'car_sales';
    config.headers['X-Business-Unit'] = activeUnit;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }
        const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        localStorage.setItem('accessToken', data.data.accessToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`;
        return api(originalRequest);
      } catch (err) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: (data) => api.post('/auth/logout', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Products
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  uploadImages: (id, formData) => api.post(`/products/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (id, imageId) => api.delete(`/products/${id}/images/${imageId}`),
  setPrimaryImage: (id, imageId) => api.patch(`/products/${id}/images/${imageId}/primary`),
};

// Sales
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  approve: (id) => api.patch(`/sales/${id}/approve`),
  updateStatus: (id, status) => api.patch(`/sales/${id}/status`, { status }),
  recordPayment: (id, data) => api.post(`/sales/${id}/payments`, data),
};

// Customers
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  uploadDocuments: (id, formData) => api.post(`/customers/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDocuments: (id) => api.get(`/customers/${id}/documents`),
  deleteDocument: (id, documentId) => api.delete(`/customers/${id}/documents/${documentId}`),
  addInteraction: (id, data) => api.post(`/customers/${id}/interactions`, data),
};

// Suppliers
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  getPurchaseOrders: (params) => api.get('/suppliers/purchase-orders', { params }),
  createPurchaseOrder: (data) => api.post('/suppliers/purchase-orders', data),
  approvePurchaseOrder: (id) => api.patch(`/suppliers/purchase-orders/${id}/approve`),
  receivePurchaseOrder: (id, data) => api.post(`/suppliers/purchase-orders/${id}/receive`, data),
  recordPOPayment: (id, data) => api.post(`/suppliers/purchase-orders/${id}/payment`, data),
  getPOItems: (id) => api.get(`/suppliers/purchase-orders/${id}/items`),
};

// Inventory (only warehouse & stock movement – no unit endpoints)
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  adjustStock: (data) => api.post('/inventory/adjust', data),
  transferStock: (data) => api.post('/inventory/transfer', data),
  getMovements: (params) => api.get('/inventory/movements', { params }),
  getTransferRequests: (params) => api.get('/inventory/transfer-requests', { params }),
  approveTransferRequest: (id) => api.patch(`/inventory/transfer-requests/${id}/approve`),
  rejectTransferRequest: (id, data) => api.patch(`/inventory/transfer-requests/${id}/reject`, data),
  getWarehouses: () => api.get('/inventory/warehouses'),
  createWarehouse: (data) => api.post('/inventory/warehouses', data),
};

// Unit Products (individual item tracking) – FIXED: uncommented and ready to use
export const unitProductsAPI = {
  getAll: (params) => api.get('/unit-products', { params }),
  getById: (id) => api.get(`/unit-products/${id}`),
  create: (data) => api.post('/unit-products', data),
  update: (id, data) => api.put(`/unit-products/${id}`, data),
  delete: (id) => api.delete(`/unit-products/${id}`),
  transfer: (id, data) => api.post(`/unit-products/${id}/transfer`, data),
};

// Reports
export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getSales: (params) => api.get('/reports/sales', { params }),
  getInventory: () => api.get('/reports/inventory'),
  getStaffPerformance: (params) => api.get('/reports/staff-performance', { params }),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getCustomers: () => api.get('/reports/customers'),
  getSalesByProductType: (params) => api.get('/reports/sales-by-product-type', { params }),
  getSalesByUser: (params) => api.get('/reports/sales-by-user', { params }),
  getProductExpenses: (params) => api.get('/reports/product-expenses', { params }),
  getProductRevenue: (params) => api.get('/reports/product-revenue', { params }),
  getTopCustomers: (params) => api.get('/reports/top-customers', { params }),
  getMostSoldProducts: (params) => api.get('/reports/most-sold-products', { params }),
  getTopStores: (params) => api.get('/reports/top-stores', { params }),
  getLowestStores: (params) => api.get('/reports/lowest-stores', { params }),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  create: (data) => api.post('/invoices', data),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  updateStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  getRoles: () => api.get('/users/roles'),
  getAuditLogs: (params) => api.get('/users/audit-logs', { params }),
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
};

// Spare Parts API
export const spAPI = {
  getDashboard: () => api.get('/sp/dashboard'),
  getCategories: () => api.get('/sp/categories'),
  getProducts: (params) => api.get('/sp/products', { params }),
  getProduct: (id) => api.get(`/sp/products/${id}`),
  createProduct: (data) => api.post('/sp/products', data),
  updateProduct: (id, data) => api.put(`/sp/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/sp/products/${id}`),
  getInventory: (params) => api.get('/sp/inventory', { params }),
  adjustStock: (data) => api.post('/sp/inventory/adjust', data),
  getCustomers: (params) => api.get('/sp/customers', { params }),
  createCustomer: (data) => api.post('/sp/customers', data),
  updateCustomer: (id, data) => api.put(`/sp/customers/${id}`, data),
  getOrders: (params) => api.get('/sp/orders', { params }),
  getOrder: (id) => api.get(`/sp/orders/${id}`),
  createOrder: (data) => api.post('/sp/orders', data),
  updateOrderStatus: (id, status) => api.patch(`/sp/orders/${id}/status`, { status }),
  recordPayment: (id, data) => api.post(`/sp/orders/${id}/payments`, data),
  getPaymentMethods: () => api.get('/sp/payment-methods'),
  downloadInvoice: (id) => api.get(`/sp/orders/${id}/invoice/pdf`, { responseType: 'blob' }),
  getSuppliers: (params) => api.get('/sp/suppliers', { params }),
  createSupplier: (data) => api.post('/sp/suppliers', data),
};

// Staff API
export const staffAPI = {
  getAll: (params) => api.get('/staff', { params }),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  generateCompanyId: () => api.get('/staff/generate-company-id'),
  uploadDocument: (id, formData) => api.post(`/staff/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDocument: (id, documentId) => api.delete(`/staff/${id}/documents/${documentId}`),
  setPrimaryDocument: (id, documentId) => api.patch(`/staff/${id}/documents/${documentId}/primary`),
};

// Warehouse Assets API
export const warehouseAssetsAPI = {
  getAll: (params) => api.get('/warehouse-assets', { params }),
  getById: (id) => api.get(`/warehouse-assets/${id}`),
  create: (data) => api.post('/warehouse-assets', data),
  update: (id, data) => api.put(`/warehouse-assets/${id}`, data),
  delete: (id) => api.delete(`/warehouse-assets/${id}`),
  getSummary: (params) => api.get('/warehouse-assets/summary', { params }),
};

// Payroll API
export const payrollAPI = {
  getAll: (params) => api.get('/payroll', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  create: (data) => api.post('/payroll', data),
  delete: (id) => api.delete(`/payroll/${id}`),
};
